import { create } from "zustand";
import { NotificationSummaryDto } from "../services/admin/types/notificationSummary.types";

interface UnifiedNotificationState {
  // Notification counts
  pendingOrdersCount: number;
  pendingLateCheckoutsCount: number;
  pendingServiceRequestsCount: number;
  unreadChatsCount: number;
  pendingTransferRequestsCount: number;
  pendingActivityBookingsCount: number;
  pendingQueueItemsCount: number;
  todayCheckinsCount: number;
  todayCheckinsBaseline: number; // locally acknowledged baseline
  fetchedAtUtc: string | null;

  // Loading state
  isLoading: boolean;
  error: string | null;

  // Actions
  setSummary: (summary: NotificationSummaryDto) => void;
  updateCount: (
    type: keyof Pick<
      UnifiedNotificationState,
      | "pendingOrdersCount"
      | "pendingLateCheckoutsCount"
      | "pendingServiceRequestsCount"
      | "unreadChatsCount"
      | "pendingTransferRequestsCount"
      | "pendingActivityBookingsCount"
      | "pendingQueueItemsCount"
    >,
    count: number,
  ) => void;
  updateFromSignalR: (type: string, count: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setTodayCheckinsBaseline: (baseline: number) => void;
  reset: () => void;
}

const initialState = {
  pendingOrdersCount: 0,
  pendingLateCheckoutsCount: 0,
  pendingServiceRequestsCount: 0,
  unreadChatsCount: 0,
  pendingTransferRequestsCount: 0,
  pendingActivityBookingsCount: 0,
  pendingQueueItemsCount: 0,
  todayCheckinsCount: 0,
  todayCheckinsBaseline: 0,
  fetchedAtUtc: null,
  isLoading: false,
  error: null,
};

export const useUnifiedNotificationStore = create<UnifiedNotificationState>(
  (set) => ({
    ...initialState,

    setSummary: (summary) =>
      set({
        pendingOrdersCount: summary.pendingOrdersCount,
        pendingLateCheckoutsCount: summary.pendingLateCheckoutsCount,
        pendingServiceRequestsCount: summary.pendingServiceRequestsCount,
        unreadChatsCount: summary.unreadChatsCount,
        pendingTransferRequestsCount: summary.pendingTransferRequestsCount,
        pendingActivityBookingsCount: summary.pendingActivityBookingsCount,
        pendingQueueItemsCount: summary.pendingQueueItemsCount,
        todayCheckinsCount: summary.todayCheckinsCount,
        fetchedAtUtc: summary.fetchedAtUtc,
        error: null,
      }),

    updateCount: (type, count) =>
      set((state) => ({
        ...state,
        [type]: count,
      })),

    updateFromSignalR: (type, count) =>
      set((state) => {
        // Map SignalR event types to store properties
        const typeMapping: Record<
          string,
          keyof Pick<
            UnifiedNotificationState,
            | "pendingOrdersCount"
            | "pendingLateCheckoutsCount"
            | "pendingServiceRequestsCount"
            | "unreadChatsCount"
            | "pendingTransferRequestsCount"
            | "pendingActivityBookingsCount"
            | "pendingQueueItemsCount"
            | "todayCheckinsCount"
          >
        > = {
          orders: "pendingOrdersCount",
          lateCheckouts: "pendingLateCheckoutsCount",
          serviceRequests: "pendingServiceRequestsCount",
          chats: "unreadChatsCount",
          transferRequests: "pendingTransferRequestsCount",
          activityBookings: "pendingActivityBookingsCount",
          queueItems: "pendingQueueItemsCount",
          checkins: "todayCheckinsCount",
        };

        const storeKey = typeMapping[type];
        if (storeKey) {
          return { ...state, [storeKey]: count };
        }
        return state;
      }),

    setLoading: (loading) => set({ isLoading: loading }),

    setError: (error) => set({ error }),

    setTodayCheckinsBaseline: (baseline) =>
      set({ todayCheckinsBaseline: baseline }),

    reset: () => set(initialState),
  }),
);
